import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { LayoutDashboard, BarChart3, Globe } from 'lucide-react';

// Разделы вкладки «Аналитика»: идут подряд на одной странице,
// подпункты в боковом меню прокручивают к ним. Новый раздел — новая запись здесь.
export const DASH_SECTIONS = [
  { id: 'overview', icon: LayoutDashboard, title: 'App.dash_overview', desc: 'App.dash_overview_desc' },
  { id: 'alerts', icon: BarChart3, title: 'App.dash_alerts', desc: 'App.dash_alerts_desc' },
  // nav — короткая подпись для меню, если заголовок раздела не помещается в одну строку
  { id: 'world', icon: Globe, title: 'App.dash_world', nav: 'App.dash_world_nav', desc: 'App.dash_world_desc' },
];

export const sectionDomId = (id) => `dash-${id}`;

export const scrollToSection = (id) => {
  const el = document.getElementById(sectionDomId(id));
  if (!el) return;
  const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  el.scrollIntoView({ behavior: reduced ? 'auto' : 'smooth', block: 'start' });
};

// Какой раздел сейчас на экране: тот, что пересекает полосу на 30–40% высоты области прокрутки.
// root — элемент с прокруткой (<main>), передаётся как состояние, чтобы наблюдение
// началось, как только он появится на странице.
export function useActiveSection(root, enabled) {
  const [active, setActive] = useState(DASH_SECTIONS[0].id);
  useEffect(() => {
    if (!root || !enabled) return;
    const observer = new IntersectionObserver(entries => {
      entries.forEach(e => { if (e.isIntersecting) setActive(e.target.dataset.section); });
    }, { root, rootMargin: '-30% 0px -60% 0px' });
    DASH_SECTIONS.forEach(s => {
      const el = document.getElementById(sectionDomId(s.id));
      if (el) observer.observe(el);
    });
    return () => observer.disconnect();
  }, [root, enabled]);
  return active;
}

export function DashboardSubNav({ active }) {
  const { t } = useTranslation();
  return (
    <nav className="ml-[26px] pl-3 border-l border-gray-200 flex flex-col gap-1" aria-label={t('App.dash_sections')}>
      {DASH_SECTIONS.map(s => (
        <button
          key={s.id}
          type="button"
          onClick={() => scrollToSection(s.id)}
          aria-current={active === s.id ? 'location' : undefined}
          className={`flex items-center gap-2.5 px-3 py-2.5 rounded-2xl text-[13px] font-bold text-left press ${active === s.id ? 'bg-red-100 text-red-700' : 'text-gray-400 hover:bg-gray-100 hover:text-gray-800'}`}
        >
          <s.icon size={16} className="shrink-0" /> {t(s.nav || s.title)}
        </button>
      ))}
    </nav>
  );
}

export function DashboardSection({ id, className = '', children }) {
  const { t } = useTranslation();
  const s = DASH_SECTIONS.find(x => x.id === id);
  return (
    <section id={sectionDomId(id)} data-section={id} className={`scroll-mt-6 ${className}`}>
      <div>
        <h2 className="text-3xl font-black text-gray-900 tracking-tight leading-tight">{t(s.title)}</h2>
        <p className="text-xs text-gray-400 italic mt-1">{t(s.desc)}</p>
      </div>
      {children}
    </section>
  );
}
